import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { APIRequestService } from 'src/app/shared/services/api/apirequest.service';
import { Observable } from 'rxjs';
import { HelperService } from 'src/app/shared/services/helper.service';
import { APIURLTypes } from 'src/app/shared/enums/apiurltypes.enum';
import { apiUrlparts } from 'src/app/shared/enums/apiurlparts.const';
import {
  PaymentsOverTimeLineChart,
  PaymentPieChartApiModel,
  PaymentListApiModel
} from '@dashboard/model/paymentModel/payments-chart.model';
import { FarmersListApiModel, FarmersMaxMinDataApiModel, IndividualFarmersApiModel } from '@dashboard/model/farmerModel/farmers.model';
import { FilterParamsApiModel } from '@dashboard/model/analyticFilterModel/analytic-filter-model';
import { Region } from '@app/shared/model/commodity.model';
import { DishAnalyticsFilters, DishAnalyticsSummaryResponse, FoodWasteStateSiteInfo, ClientConfigResponse } from '../dish-analytics/dish-analytics.model';

@Injectable({
  providedIn: 'root'
})
export class AnalyticService {
  private foodWasteAnalyticsReportUrl = 'api/foodwaste/analytics/report';
  private foodWasteAnalyticsSummaryUrl = 'api/foodwaste/analytics/summary';
  private foodWasteStateSiteInfoUrl = 'api/foodwaste/v1/foodWasteState';
  private foodWasteClientConfigUrl = 'api/foodwaste/v1/config';
  private foodWasteDayPartVsWasteTypeDetails = "api/foodwaste/analytics/daypart-waste-breakdown";
  private dashboardMetricsUrl = 'api/foodwaste/analytics/dashboard';

  constructor(
    private apiRequest: APIRequestService,
    private helperService: HelperService
  ) { }

  getCommodity(): Observable<any> {
    return this.apiRequest.get('', null, this.helperService.urlFactory(APIURLTypes.DCM, (apiUrlparts.commodityGet)));
  }

  // TODO:
  public getCommoditybyCategory(categoryid?): Observable<any> {
    let params: HttpParams = new HttpParams();
    if (categoryid) {
      params = params.append('commodityCategoryId', categoryid);
    }
    return this.apiRequest.get(
      '' + params, null, this.helperService.urlFactory(APIURLTypes.DCM, (`${apiUrlparts.commodityCategory}?` + params))
    );
  }

  getCommodityList(): Observable<any> {
    let params: HttpParams = new HttpParams();
    const customerId = this.helperService.getCustomerId();
    if (customerId) {
      params = params.append('customer_id', customerId.toString());
    }
    params = params.append('p', '0');
    params = params.append('l', '1000');

    return this.apiRequest.get(
      '' + params, null, this.helperService.urlFactory(APIURLTypes.DCM, (`${apiUrlparts.commodityGet}?` + params))
    );
  }

  getStateList(): Observable<any> {
    const params: HttpParams = new HttpParams();
    // params = params.append('customer_id', this.helperService.getCustomerId().toString());
    // params = params.append('p', '0');
    // params = params.append('l', '1000');

    return this.apiRequest.get(
      '' + params, null, this.helperService.urlFactory(APIURLTypes.DCM, (`${apiUrlparts.stateList}?` + params))
    );
  }

  getDistrictList(): Observable<any> {
    let params: HttpParams = new HttpParams();    
    // params = params.append('state_id', stateid);
    return this.apiRequest.get(
      '' + params, null, this.helperService.urlFactory(APIURLTypes.DCM, (`${apiUrlparts.districtList}` + params))
    );
  }

  getSeasonList(): Observable<any> {
    let params: HttpParams = new HttpParams();
    const customerId = this.helperService.getCustomerId();
    if (customerId) {
      params = params.append('client_id', customerId.toString());
    }

    return this.apiRequest.get(
      '' + params, null, this.helperService.urlFactory(APIURLTypes.DCM, (`${apiUrlparts.seasonList}?` + params))
    );
  }

  getAnalyticTabdata(): Observable<any> {
    //const params: HttpParams = new HttpParams();
    // params = params.append('customer_id', this.helperService.getCustomerId().toString());
    // params = params.append('p', '0');
    // params = params.append('l', '1000');

    return this.apiRequest.get(
      '', null, this.helperService.urlFactory(APIURLTypes.DCM, (`${apiUrlparts.analyticTabData}`))
    );
  }

  getRegionList(
    dateFrom?: string,
    dateTo?: string
  ): Observable<Region[]> {
    let params: HttpParams = new HttpParams();
    params = params.append('p', '0');
    params = params.append('l', '1000');
    params = params.append('date_from', dateFrom);
    params = params.append('date_to', dateTo);
    return this.apiRequest.get('' + params, null, this.helperService.urlFactory(APIURLTypes.DCM, (apiUrlparts.regionList + params)));
  }

  getCollectionCenterList(
    regionId?: string,
    dateFrom?: string,
    dateTo?: string
  ): Observable<any> {
    let params: HttpParams = new HttpParams();
    params = params.append('p', '0');
    params = params.append('l', '1000');
    if (regionId !== 'null') {
      params = params.append('region_id', regionId);
    }
    params = params.append('date_from', dateFrom);
    params = params.append('date_to', dateTo);
    return this.apiRequest.get(
      '' + params, null, this.helperService.urlFactory(APIURLTypes.DCM, (apiUrlparts.commercialLocationsGet + params))
    );
  }


  getHighLowAvgTotalCollection(
    commodityId: number,
    stateId: number,
    centerId: number,
    dateFrom?: string,
    dateTo?: string,
    regionId?,
    deviceId?,
    categoryId?,
    district?
  ): Observable<any> {
    let params: HttpParams = new HttpParams();

    // params = params.append('collection_center_id', centerId.toString());
    params = params.append('date_from', dateFrom);
    params = params.append('date_to', dateTo);
    if (regionId !== 'null') {
      params = params.append('region_id', regionId);
    }

    if (deviceId) {
      params = params.append('device_serial_number', deviceId);
    }

    if (stateId > 0) {
      params = params.append('state_id', stateId.toString());
    }

    if (commodityId > 0) {
      params = params.append('commodity_id', commodityId.toString());
    }

    if (categoryId) {
      params = params.append('category_id', categoryId);
    }

    if (district) {
      params = params.append('district_name', district);
    }

    return this.apiRequest.get('', null, this.helperService.urlFactory(APIURLTypes.SCM, ('api/analytics/quantity?' + params)));
  }

  getQuantityCollectionByCenter(
    commodityId: number,
    stateId: number,
    dateFrom?: string,
    dateTo?: string,
    deviceId?,
    categoryId?,
    district?,
  ): Observable<any> {
    let params: HttpParams = new HttpParams();

    if (stateId > 0) {
      params = params.append('state_id', stateId.toString());
    }

    if (commodityId > 0) {
      params = params.append('commodity_id', commodityId.toString());
    }

    if (deviceId) {
      params = params.append('device_serial_number', deviceId);
    }

    if (categoryId) {
      params = params.append('category_id', categoryId);
    }

    if (district) {
      params = params.append('district_name', district);
    }

    params = params.append('date_from', dateFrom);
    params = params.append('date_to', dateTo);
    return this.apiRequest.get('', null, this.helperService.urlFactory(APIURLTypes.SCM, ('api/analytics/quantity/collections?' + params)));
  }

  getQuantityCollectionOvertheTime(
    commodityId: number,
    stateId: number,
    dateFrom?: string,
    dateTo?: string,
    deviceId?,
    categoryId?,
    district?
  ): Observable<any> {
    let params: HttpParams = new HttpParams();
    if (stateId > 0) {
      params = params.append('state_id', stateId.toString());
    }
    if (commodityId > 0) {
      params = params.append('commodity_id', commodityId.toString());
    }
    if (deviceId) {
      params = params.append('device_serial_number', deviceId);
    }
    if (categoryId) {
      params = params.append('category_id', categoryId);
    }

    if (district) {
      params = params.append('district_name', district);
    }

    params = params.append('date_from', dateFrom);
    params = params.append('date_to', dateTo);
    return this.apiRequest.get('', null, this.helperService.urlFactory(APIURLTypes.SCM, ('api/analytics/collections-time?' + params)));
  }

  getQuantityCollectionByCenterAndRegion(
    commodityId,
    centerId,
    dateFrom?: string,
    dateTo?: string,
    deviceId?,
    categoryId?
  ): Observable<any> {
    let params: HttpParams = new HttpParams();
    if (commodityId) {
      // params = params.append('commodity_id', commodityId);
    }

    if (centerId) {
      // params = params.append('inst_center_id', centerId);
    }

    if (deviceId) {
      params = params.append('device_serial_number', deviceId);
    }

    if (categoryId) {
      params = params.append('category_id', categoryId);
    }

    params = params.append('commodity_id', commodityId);
    // params = params.append('inst_center_id', centerId);
    params = params.append('date_from', dateFrom);
    params = params.append('date_to', dateTo);
    return this.apiRequest.get('', null, this.helperService.urlFactory(APIURLTypes.SCM, ('api/analytics/center-region?' + params)));
  }

  getQuantityMonthlyWeeklyAndQuaterlyData(
    commodityId: number,
    stateId: number,
    dateFrom,
    dateTo,
    deviceId?,
    categoryId?,
    district?
  ): Observable<any> {
    let params: HttpParams = new HttpParams();
    if (stateId > 0) {
      params = params.append('state_id', stateId.toString());
    }

    if (commodityId > 0) {
      params = params.append('commodity_id', commodityId.toString());
    }

    if (dateFrom) {
      // params = params.append('date_from', dateFrom);
    }

    if (dateTo) {
      // params = params.append('date_to', dateTo);
    }

    if (deviceId) {
      params = params.append('device_serial_number', deviceId);
    }

    if (categoryId) {
      params = params.append('category_id', categoryId);
    }

    if (district) {
      params = params.append('district_name', district);
    }

    params = params.append('date_from', dateFrom);
    params = params.append('date_to', dateTo);
    return this.apiRequest.get('', null, this.helperService.urlFactory(APIURLTypes.SCM, ('api/analytics/increment-decrement?' + params)));
  }

  getQualityOvertheTime(
    commodityId: number,
    dateFrom: number,
    dateTo: number,
    chemicalAnalytic?: string,
    deviceId?
  ): Observable<any> {
    let params: HttpParams = new HttpParams();

    params = params.append('commodity_id', commodityId.toString());
    params = params.append('analysis_name', chemicalAnalytic);
    params = params.append('date_from', dateFrom.toString());
    params = params.append('date_to', dateTo.toString());

    if (deviceId) {
      params = params.append('device_serial_number', deviceId);
    }
    // params = params.append('inst_center_id', centerId);
    return this.apiRequest.get(
      '', null, this.helperService.urlFactory(APIURLTypes.SCM, ('api/scan/filter/quality/overtime?' + params))
    );
  }

  getQualityAnalytic(commodityId: number): Observable<any> {
    let params: HttpParams = new HttpParams();
    // params = params.append('commodity_id', commodityId.toString());
    // tslint:disable-next-line: variable-name
    const commodity_id = ( typeof commodityId === 'number' || typeof commodityId === 'string')
    ? commodityId : commodityId['commodity_id']
    params = params.append('commodity_id', commodity_id.toString());
    return this.apiRequest.get('', null, this.helperService.urlFactory(APIURLTypes.DCM, (`${apiUrlparts.commodityAnalytics}?` + params)));
  }

  getQualityData(
    commodityId: number,
    dateFrom?: string,
    dateTo?: string,
    chemicalAnalytic?,
    deviceId?
  ): Observable<any> {
    let params: HttpParams = new HttpParams();
    // if (commodityId) {
    //   // params = params.append('commodity_id', commodityId);
    // }

    // if (chemicalAnalytic) {
    //   // params = params.append('analysis_name', chemicalAnalytic);
    // }
    params = params.append('analysis_name', chemicalAnalytic);
    params = params.append('commodity_id', commodityId.toString());
    params = params.append('date_from', dateFrom);
    params = params.append('date_to', dateTo);
    if (deviceId) {
      params = params.append('device_serial_number', deviceId);
    }
    return this.apiRequest.get('', null, this.helperService.urlFactory(APIURLTypes.SCM, ('api/scan/filter?' + params)));
  }

  getQualityOverTheTime(commodityId, chemicalAnalytic): Observable<any> {
    let params: HttpParams = new HttpParams();
    if (commodityId) {
      // params = params.append('commodity_id', commodityId);
    }

    if (chemicalAnalytic) {
      // params = params.append('analysis_name', chemicalAnalytic);
    }
    params = params.append('analysis_name', 'amylose');
    params = params.append('commodity_id', '1');
    return this.apiRequest.get('', null, this.helperService.urlFactory(APIURLTypes.SCM, ('api/scan/filter?' + params)));
  }


  getQualityGradeData(commodityId: number,
                      dateFrom?: string,
                      dateTo?: string,
                      deviceId?: string): Observable<any> {
    let params: HttpParams = new HttpParams();
    params = params.append('commodity_id', commodityId.toString());
    params = params.append('date_from', dateFrom);
    params = params.append('date_to', dateTo);
    if (deviceId) {
      params = params.append('device_serial_number', deviceId);
    }
    return this.apiRequest.get('', null, this.helperService.urlFactory(APIURLTypes.SCM, ('api/scan/filter/quality/grade?' + params)));
  }

  // getQualityGradeTableData(commodityId, chemicalAnalytic): Observable<any> {
  //   let params: HttpParams = new HttpParams();

  //   if (commodityId) {
  //     // params = params.append('commodity_id', commodityId);
  //   }

  //   if (chemicalAnalytic) {
  //     // params = params.append('analysis_name', chemicalAnalytic);
  //   }
  //   params = params.append('commodity_id', '30');
  //   params = params.append('analysis_name', 'fat');
  //   // params = params.append('commodity_id', '1');
  //   return this.apiRequest.get(
  //     '', null, this.helperService.urlFactory(APIURLTypes.SCM, ('api/scan/filter/quality/overtime?' + params))
  //   );
  // }

  public getPaymentsOvertheTime(filter: FilterParamsApiModel): Observable<PaymentsOverTimeLineChart[]> {
    let params: HttpParams = new HttpParams();
    if (filter.commodity_id) {
      params = params.append('commodity_id', filter.commodity_id.toString());
    }
    params = params.append('commodity_id', '4');
    return this.apiRequest.get('', null, this.helperService.urlFactory(APIURLTypes.SCM, ('api/analytics/payment-overtime?' + params)));
    // please don't remove this uncomment once the data is available for particular params
    // return this.apiRequest.get('', null, this.helper.urlFactory(
    //   APIURLTypes.SCM, ('api/analytics/payment-overtime?' + setHttpParamsOfAPI(filter, ))
    // ));
  }

  public getPaymentsPieData(filter: FilterParamsApiModel): Observable<PaymentPieChartApiModel[]> {
    // const params = '?commodity_id=4&customer_id=1';
    let params: HttpParams = new HttpParams();
    if (filter.commodity_id) {
      params = params.append('commodity_id', filter.commodity_id.toString());
    }
    // params = params.append('commodity_id', '4');
    // params = params.append('customer_id', '1');

    // please don't remove this uncomment once the data is available for particular params
    // return this.apiRequest.get('', null, this.helper.urlFactory(
    //   APIURLTypes.SCM, ('api/analytics/payment-chart?' + setHttpParamsOfAPI(filter, ))
    // ));
    return this.apiRequest.get('', null, this.helperService.urlFactory(APIURLTypes.SCM, ('api/analytics/payment-chart?' + params)));
  }

  public getPaymentsList(filter: FilterParamsApiModel): Observable<PaymentListApiModel[]> {
    let params: HttpParams = new HttpParams();
    if (filter.commodity_id) {
      params = params.append('commodity_id', filter.commodity_id.toString());
    }
    return this.apiRequest.get('', null, this.helperService.urlFactory(APIURLTypes.SCM, ('api/analytics/payment-list?' + params)));
    // please don't remove this uncomment once the data is available for particular params
    // return this.apiRequest.get('', null, this.helper.urlFactory(
    //   APIURLTypes.SCM, ('api/analytics/payment-list?' + setHttpParamsOfAPI(filter, ))
    // ));
  }

  public getFarmersList(
    commodity?: object,
    dateFrom?: string,
    dateTo?: string
  ): Observable<FarmersListApiModel[]> {
    let params: HttpParams = new HttpParams();
    // tslint:disable-next-line: no-string-literal
    params = params.append('commodity_id', commodity['commodityId'].toString());
    // params = params.append('analysis_name', commodity['analysisName']);
    params = params.append('date_from', dateFrom);
    params = params.append('date_to', dateTo);
    return this.apiRequest.get('', null, this.helperService.urlFactory(APIURLTypes.SCM, (`${apiUrlparts.farmerList}?` + params)));
  }

  public getSelectedFarmerDataBasedOnQuantityAndQuality(
    commodity: object,
    farmerID: number,
    dateFrom: string,
    dateTo: string
  ): Observable<IndividualFarmersApiModel> {
    let params: HttpParams = new HttpParams();
    // tslint:disable-next-line: no-string-literal
    params = params.append('commodity_id', commodity['commodityId'].toString());
    // tslint:disable-next-line: no-string-literal
    params = params.append('analysis_code', commodity['analysisName']);
    params = params.append('farmer_id', farmerID.toString());
    params = params.append('date_from', dateFrom);
    params = params.append('date_to', dateTo);
    return this.apiRequest.get('', null, this.helperService.urlFactory(APIURLTypes.SCM, (`${apiUrlparts.farmerMaxMin}?` + params)));
  }

  public getFarmersMaxMinData(
    commodityId: number,
    regionId: number | string,
    dateFrom: string,
    dateTo: string
  ): Observable<FarmersMaxMinDataApiModel> {
    let params: HttpParams = new HttpParams();
    if (commodityId) {
      // params = params.append('commodity_id', commodityId);
    }
    if (regionId !== 'null') {
      // params = params.append('customer_id', );
    }
    params = params.append('commodity_id', commodityId.toString());
    params = params.append('region_id', '25');
    params = params.append('date_from', dateFrom);
    params = params.append('date_to', dateTo);
    return this.apiRequest.get('', null, this.helperService.urlFactory(APIURLTypes.SCM, ('api/analytics/farmers?' + params)));
  }

  public getFarmersMaxMinDataQuality(
    commodityId: number,
    regionId: number | string,
    dateFrom: string,
    dateTo: string,
    analysisCode: string
  ): Observable<FarmersMaxMinDataApiModel> {
    let params: HttpParams = new HttpParams();
    if (commodityId) {
      // params = params.append('commodity_id', commodityId);
    }
    if (regionId !== 'null') {
      // params = params.append('customer_id', );
    }
    params = params.append('commodity_id', commodityId.toString());
    params = params.append('region_id', '25');
    params = params.append('date_from', dateFrom);
    params = params.append('date_to', dateTo);
    params = params.append('analysis_code', analysisCode);
    return this.apiRequest.get('', null, this.helperService.urlFactory(APIURLTypes.SCM, ('api/analytics/farmers/quality?' + params)));
  }

  getCustomers(): Observable<any> {
    let params: HttpParams = new HttpParams();
    params = params.append('p', '0');
    params = params.append('l', '1000');
    return this.apiRequest.get('', null, this.helperService.urlFactory(APIURLTypes.IAM, ('api/customer?' + params)));
  }

  getUsers(): Observable<any> {
    let params: HttpParams = new HttpParams();
    params = params.append('p', '0');
    params = params.append('l', '1000');
    return this.apiRequest.get('', null, this.helperService.urlFactory(APIURLTypes.IAM, ('api/user?' + params)));
  }

  getScanHistory(commodityId, customerId): Observable<any> {
    let params: HttpParams = new HttpParams();
    // params = params.append('customer_id', customer_id);
    // params = params.append('commodity_id', commodity_id);
    params = params.append('commodity_id', '4');
    return this.apiRequest.get('', null, this.helperService.urlFactory(APIURLTypes.SCM, (apiUrlparts.scanHistory + params)));
  }

  getDeviceDataSuperAdmin(): Observable<any> {
    let params: HttpParams = new HttpParams();
    params = params.append('p', '0');
    params = params.append('l', '1000');
    return this.apiRequest.get('', null, this.helperService.urlFactory(APIURLTypes.DCM, (apiUrlparts.devicesWidget + params)));
  }

  getDeviceInventoryDataSuperAdmin(): Observable<any> {
    let params: HttpParams = new HttpParams();
    params = params.append('p', '0');
    params = params.append('l', '1000');
    return this.apiRequest.get('', null, this.helperService.urlFactory(APIURLTypes.DCM, (apiUrlparts.devicesInventoryWidget + params)));
  }

  getCustomerWidget(): Observable<any> {
    let params: HttpParams = new HttpParams();
    params = params.append('p', '0');
    params = params.append('l', '1000');
    return this.apiRequest.get('', null, this.helperService.urlFactory(APIURLTypes.IAM, (apiUrlparts.customerWidget + params)));
  }

  getUserWidget(): Observable<any> {
    let params: HttpParams = new HttpParams();
    params = params.append('p', '0');
    params = params.append('l', '1000');
    return this.apiRequest.get('', null, this.helperService.urlFactory(APIURLTypes.IAM, (apiUrlparts.usersWidget + params)));
  }

  getPOWidget(): Observable<any> {
    let params: HttpParams = new HttpParams();
    params = params.append('p', '0');
    params = params.append('l', '1000');
    return this.apiRequest.get('', null, this.helperService.urlFactory(APIURLTypes.DCM, (apiUrlparts.poDetails + params)));
  }

  getFoodWasteStateSiteInfo(): Observable<FoodWasteStateSiteInfo[]> {
    const apiUrlObject = this.helperService.urlFactory(APIURLTypes.DCM, this.foodWasteStateSiteInfoUrl);
    return this.apiRequest.get(this.foodWasteStateSiteInfoUrl, null, apiUrlObject);
  }

  getClientConfig(): Observable<ClientConfigResponse> {
    const apiUrlObject = this.helperService.urlFactory(APIURLTypes.DCM, this.foodWasteClientConfigUrl);
    return this.apiRequest.get(this.foodWasteClientConfigUrl, null, apiUrlObject);
  }

  getDishAnalyticsSummary(filters: DishAnalyticsFilters): Observable<DishAnalyticsSummaryResponse> {
    const apiUrlObject = this.helperService.urlFactory(APIURLTypes.DCM, this.foodWasteAnalyticsSummaryUrl);
    
    const queryParams: any = {};
    for (const key in filters) {
      if (filters[key] !== undefined && filters[key] !== null) {
        const filterValue = (filters as any)[key]; 
        if (Array.isArray(filterValue)) {
          if (filterValue.length > 0) { 
             queryParams[key] = filterValue.join(',');
          }
        } else {
          queryParams[key] = filterValue;
        }
      }
    }
        
    return this.apiRequest.get(this.foodWasteAnalyticsSummaryUrl, queryParams, apiUrlObject);
  }

  getReport(filters: DishAnalyticsFilters): Observable<any> {
    const apiUrlObject = this.helperService.urlFactory(APIURLTypes.DCM, this.foodWasteAnalyticsReportUrl);
    const queryParams: any = {};
    
    for (const key in filters) {
      if (filters[key] !== undefined && filters[key] !== null) {
        const filterValue = (filters as any)[key]; 
        if (Array.isArray(filterValue)) {
          if (filterValue.length > 0) { 
             queryParams[key] = filterValue.join(',');
          }
        } else {
          queryParams[key] = filterValue;
        }
      }
    }
    console.log({ queryParams: queryParams });
    // Add download flag outside the loop
    queryParams['isDownload'] = true;
        
    return this.apiRequest.getBlobWithParams(this.foodWasteAnalyticsReportUrl, queryParams, apiUrlObject);
  }

  getDayPartAndWasteTypeDetails(data: { 
    start_date_epoch_ms: number; 
    end_date_epoch_ms: number; 
    installation_center_ids: number[]; 
    dish_category_ids: number[]; 
    states: number[];
    day_parts: string[];
    food_waste_types: string[];
    dish_category_names: string[];
    regions: string[];
    machine_locations: string[];
  }): Observable<any> {
    let params: HttpParams = new HttpParams();
    
    // Add required parameters
    if (data.start_date_epoch_ms) {
      params = params.append('start_date_epoch_ms', data.start_date_epoch_ms.toString());
    }
    if (data.end_date_epoch_ms) {
      params = params.append('end_date_epoch_ms', data.end_date_epoch_ms.toString());
    }
    
    // Add optional array parameters
    if (data.states && data.states.length > 0) {
      data.states.forEach(state => {
        params = params.append('states', state);
      });
    }
    
    if (data.installation_center_ids && data.installation_center_ids.length > 0) {
      data.installation_center_ids.forEach(id => {
        params = params.append('installation_center_ids', id.toString());
      });
    }
    
    if (data.day_parts && data.day_parts.length > 0) {
      data.day_parts.forEach(dayPart => {
        params = params.append('day_parts', dayPart);
      });
    }
    
    if (data.food_waste_types && data.food_waste_types.length > 0) {
      data.food_waste_types.forEach(wasteType => {
        params = params.append('food_waste_types', wasteType);
      });
    }
    
    if (data.dish_category_ids && data.dish_category_ids.length > 0) {
      data.dish_category_ids.forEach(id => {
        params = params.append('dish_category_ids', id.toString());
      });
    }
    
    if (data.dish_category_names && data.dish_category_names.length > 0) {
      data.dish_category_names.forEach(name => {
        params = params.append('dish_category_names', name);
      });
    }
    
    if (data.regions && data.regions.length > 0) {
      data.regions.forEach(region => {
        params = params.append('regions', region);
      });
    }
    
    if (data.machine_locations && data.machine_locations.length > 0) {
      data.machine_locations.forEach(location => {
        params = params.append('machine_locations', location);
      });
    }
    
    const apiUrlObject = this.helperService.urlFactory(APIURLTypes.DCM, this.foodWasteDayPartVsWasteTypeDetails + '?' + params);
    return this.apiRequest.get(this.foodWasteDayPartVsWasteTypeDetails, null, apiUrlObject);
  }

  /**
   * Get dashboard metrics for summary table
   * @param siteIds - The site IDs to get metrics for
   * @param dateFrom - The date to get metrics from
   * @param userId - The user ID to filter by
   * @returns Observable with dashboard metrics data
   */
  getDashboardMetrics(siteIds: number[], dateFrom: number, userId?: any): Observable<any> {
    let params: HttpParams = new HttpParams();
    if(siteIds != null) {
      siteIds.forEach(id => {
        params = params.append('siteId', id);
      });
    }
    if(dateFrom != null) {
      params = params.append('dateFrom', dateFrom);
    }
    if(userId) {
      params = params.append('userId', userId);
    }
    const apiUrlObject = this.helperService.urlFactory(APIURLTypes.DCM, this.dashboardMetricsUrl + '?' + params);
    return this.apiRequest.get(this.dashboardMetricsUrl, null, apiUrlObject);
  }

  getDeviceLocation(deviceSerialNos: string, dateFrom: number, dateTo: number): Observable<any> {
    let params: HttpParams = new HttpParams();
    params = params.append('device_serial_no', deviceSerialNos);
    params = params.append('date_from', dateFrom.toString());
    params = params.append('date_to', dateTo.toString());
    const customerId = this.helperService.getCustomerId();
    if (customerId) {
      params = params.append('customerId', customerId.toString());
    }
    return this.apiRequest.get('', null, this.helperService.urlFactory(APIURLTypes.DCM, ('api/scan-metadata/device-location?' + params)));
  }

  // Tea 
  
  getOwnTeaRecords(p,l){
    let params: HttpParams = new HttpParams();
    params = params.append('p', p);
    params = params.append('l', l);
    return this.apiRequest.get('', null, this.helperService.urlFactory(APIURLTypes.DCM, (apiUrlparts.owntealist + params)));
  }

  getGardensForDropdown(p,l){
    let params: HttpParams = new HttpParams();
    params = params.append('p', p);
    params = params.append('l', l);
    return this.apiRequest.get('', null, this.helperService.urlFactory(APIURLTypes.DCM, (apiUrlparts.getGardens + params)));
  }

  getSardaarsForDropdown(p,l){
    let params: HttpParams = new HttpParams();
    params = params.append('p', p);
    params = params.append('l', l);
    return this.apiRequest.get('', null, this.helperService.urlFactory(APIURLTypes.DCM, (apiUrlparts.getSardaars + params)));
  }

  getOperatorTypeofUserForDropdown(p,l){
    let params: HttpParams = new HttpParams();
    params = params.append('p', p);
    params = params.append('l', l);
    return this.apiRequest.get('', null, this.helperService.urlFactory(APIURLTypes.DCM, (apiUrlparts.getSardaars + params)));
  }

  postSection(data) {
    //console.log(new Date(this.formsection.controls['year_of_planting'].value));
    let tempdate = new Date(data.year_of_planting);
    data.year_of_planting = tempdate.getTime();
    if (data.section_id) {
      //const datap = this.deviceAdapter.reverseAdapt(data);
      return this.apiRequest.put('', data, this.helperService.urlFactory(APIURLTypes.DCM, (apiUrlparts.postsection + '/' + data.section_id)));
    } else {
     // const datap = this.deviceAdapter.reverseAdapt(data);
      return this.apiRequest.post('', data, this.helperService.urlFactory(APIURLTypes.DCM, (apiUrlparts.postsection)));
    }
  }

  getSectionById(id): Observable<any> {
    return this.apiRequest.get('', null, this.helperService.urlFactory(APIURLTypes.DCM, (apiUrlparts.getsection+'/' + id)));
  }

  deleteSection(id): Observable<any> {
    return this.apiRequest.delete('', this.helperService.urlFactory(APIURLTypes.DCM, (apiUrlparts.deletesection+'/' + id)));
  }

  exportData(formData: any): Observable<any> {
    const customerId = this.helperService.getCustomerId();
    let params: HttpParams = new HttpParams();
    if (customerId) {
      params = params.append('customerId', customerId.toString());
    }
    return this.apiRequest.postPdf('', formData, this.helperService.urlFactory(APIURLTypes.DCM, (apiUrlparts.exportData + '?' + params)));
  }




  getBoughtTeaRecords(p,l){
    let params: HttpParams = new HttpParams();
    params = params.append('p', p);
    params = params.append('l', l);
    return this.apiRequest.get('', null, this.helperService.urlFactory(APIURLTypes.DCM, (apiUrlparts.boughttealist + params)));
  }

  postSupplier(data) {
    console.log({'postSupplier':data});
    if (data.supplier_id) {
      //const datap = this.deviceAdapter.reverseAdapt(data);
      return this.apiRequest.put('', data, this.helperService.urlFactory(APIURLTypes.DCM, (apiUrlparts.postsupplier + '/' + data.supplier_id)));
    } else {
     // const datap = this.deviceAdapter.reverseAdapt(data);
      return this.apiRequest.post('', data, this.helperService.urlFactory(APIURLTypes.DCM, (apiUrlparts.postsupplier)));
    }
  }

  postFarmer(data):Observable<any>{
    if(data.id){
      return this.apiRequest.put('', data, this.helperService.urlFactory(APIURLTypes.DCM, (apiUrlparts.postfarmer+'/' + data.id)));
    }
    else{
      return this.apiRequest.post('', data, this.helperService.urlFactory(APIURLTypes.DCM, (apiUrlparts.postfarmer)));
    }
    
  }

  postVehicle(data):Observable<any>{
    if(data.id){
      return this.apiRequest.put('', data, this.helperService.urlFactory(APIURLTypes.DCM, (apiUrlparts.verhicleapi+'/' + data.id)));
    }
    else{
      return this.apiRequest.post('', data, this.helperService.urlFactory(APIURLTypes.DCM, (apiUrlparts.verhicleapi)));
    }
    
  }

  getFarmers(p,l,supplierId?):Observable<object[]>{
    let params: HttpParams = new HttpParams();
    params = params.append('p', p);
    params = params.append('l', l);
    if(supplierId){
    params =  params.append('supplier_id',supplierId);
    }
    return this.apiRequest.get('', null, this.helperService.urlFactory(APIURLTypes.DCM, (apiUrlparts.postfarmer + '?'+params)));
  }

  getVehicles(p,l,supplierId?):Observable<object[]>{
    let params: HttpParams = new HttpParams();
    params = params.append('p', p);
    params = params.append('l', l);

    if(supplierId){
      params =  params.append('supplier_id',supplierId);
      }
      
    return this.apiRequest.get('', null, this.helperService.urlFactory(APIURLTypes.DCM, (apiUrlparts.verhicleapi + '?'+params)));
  }

  getSupplierById(id): Observable<any> {
    return this.apiRequest.get('', null, this.helperService.urlFactory(APIURLTypes.DCM, (apiUrlparts.getsupplier+'/' + id)));
  }

  getFarmerById(id): Observable<any> {
    return this.apiRequest.get('', null, this.helperService.urlFactory(APIURLTypes.DCM, (apiUrlparts.postfarmer+'/' + id)));
  }

  getVehicleById(id): Observable<any> {
    return this.apiRequest.get('', null, this.helperService.urlFactory(APIURLTypes.DCM, (apiUrlparts.verhicleapi+'/' + id)));
  }

  deleteFarmer(id): Observable<any> {
    return this.apiRequest.delete('', this.helperService.urlFactory(APIURLTypes.DCM, (apiUrlparts.deletefarmer+'/' + id)));
  }

  deleteVehicle(id): Observable<any> {
    return this.apiRequest.delete('', this.helperService.urlFactory(APIURLTypes.DCM, (apiUrlparts.verhicleapi+'/' + id)));
  }

  deleteSupplier(id): Observable<any> {
    return this.apiRequest.delete('', this.helperService.urlFactory(APIURLTypes.DCM, (apiUrlparts.deletesupplier+'/' + id)));
  }

  getSectionDetails(): Observable<any> {
    return this.apiRequest.get('', null, this.helperService.urlFactory(APIURLTypes.DCM, (apiUrlparts.allSections)));
  }

  getEstateDetails(): Observable<any> {
    // Return mock data for testing
    return this.apiRequest.get('', null, this.helperService.urlFactory(APIURLTypes.DCM, (apiUrlparts.estateDetails)));
  }

  getSupplierDetails(): Observable<any> {
    return this.apiRequest.get('', null, this.helperService.urlFactory(APIURLTypes.DCM, (apiUrlparts.supplierDetails)));
  }

  getEstateContacts(sectionId: string): Observable<any> {
    let params: HttpParams = new HttpParams();
    params = params.append('sectionId', sectionId);
    const customerId = this.helperService.getCustomerId();
    if (customerId) {
      params = params.append('customerId', customerId.toString());
    }
    
    return this.apiRequest.get('', null, this.helperService.urlFactory(APIURLTypes.DCM, (apiUrlparts.estateContacts + params)));
  }

  getSupplierContacts(sectionId: string): Observable<any> {
    let params: HttpParams = new HttpParams();
    params = params.append('sectionId', sectionId);
    const customerId = this.helperService.getCustomerId();
    if (customerId) {
      params = params.append('customer_id', customerId.toString());
    }
    return this.apiRequest.get('', null, this.helperService.urlFactory(APIURLTypes.DCM, (apiUrlparts.supplierContacts + params)));
  }

  downloadEstateContacts(): Observable<any> {
    const customerId = this.helperService.getCustomerId();
    let params: HttpParams = new HttpParams();
    if (customerId) {
      params = params.append('customerId', customerId.toString());
    }
    return this.apiRequest.getPdf('', null, this.helperService.urlFactory(APIURLTypes.DCM, (apiUrlparts.downloadEstateContacts + '?' + params)));
  }

  uploadEstateContacts(formData: FormData, file: File): Observable<any> {
    console.log('uploadEstateContacts', formData);
    const customerId = this.helperService.getCustomerId();
    let params: HttpParams = new HttpParams();
    if (customerId) {
      params = params.append('customerId', customerId.toString());
    }
    return this.apiRequest.postMultipart('', file, formData, this.helperService.urlFactory(APIURLTypes.DCM, (apiUrlparts.uploadEstateContacts + '?' + params)));
  }

  downloadSupplierContacts(): Observable<any> {
    const customerId = this.helperService.getCustomerId();
    let params: HttpParams = new HttpParams();
    if (customerId) {
      params = params.append('customerId', customerId.toString());
    }
    return this.apiRequest.getPdf('', null, this.helperService.urlFactory(APIURLTypes.DCM, (apiUrlparts.downloadSupplierContacts + '?' + params)));
  }

  uploadSupplierContacts(formData: FormData, file: File): Observable<any> {
    console.log('uploadSupplierContacts', formData);
    const customerId = this.helperService.getCustomerId();
    let params: HttpParams = new HttpParams();
    if (customerId) {
      params = params.append('customerId', customerId.toString());
    }
    return this.apiRequest.postMultipart('', file, formData, this.helperService.urlFactory(APIURLTypes.DCM, (apiUrlparts.uploadSupplierContacts + '?' + params)));
  }

  downloadFactoryContacts(): Observable<any> {
    const customerId = this.helperService.getCustomerId();
    let params: HttpParams = new HttpParams();
    if (customerId) {
      params = params.append('customerId', customerId.toString());
    }
    return this.apiRequest.getPdf('', null, this.helperService.urlFactory(APIURLTypes.DCM, (apiUrlparts.downloadFactoryContacts + '?' + params)));
  }

  uploadFactoryContacts(formData: FormData, file: File): Observable<any> {
    console.log('uploadFactoryContacts', formData);
    const customerId = this.helperService.getCustomerId();
    let params: HttpParams = new HttpParams();
    if (customerId) {
      params = params.append('customerId', customerId.toString());
    }
    return this.apiRequest.postMultipart('', file, formData, this.helperService.urlFactory(APIURLTypes.DCM, (apiUrlparts.uploadFactoryContacts + '?' + params)));
  }
}
